// Components==============
import updateMessage from 'actions/message/update';
import indexMessage from 'actions/message/index';
import storeMessage from 'actions/message/store';
import indexNotification from 'actions/notification/index';
import Chat from 'components/UI/Communication/Chat/Chat';
import { useAlert } from 'hooks/useAlert';
import useGenerateEventName from 'hooks/useGenerateEventName';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import { useStorage } from 'hooks/useStorage';
import { useTranslate } from 'hooks/useTranslate';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ProcesSteps } from 'typings';
import echo from '../../../../../echo';
import { ProjectDetailsContext } from '../../ProjectDetails';
import './Communication.module.scss';
import ChatList from './components/ChatList/ChatList';
// =========================

type ChannelEntityType = {
  type: ChannelTargetType;
  id: number;
  name?: string;
  image?: string;
  phase?: ProcesSteps;
  member?: UserEntity;
};

export type ChannelType = ChannelEntityType & {
  activities?: ChannelEntityType[];
};

export type OnlineUserType = { name: string; id: number };

type OnlineUserEventType =
  | {
      action: 'here';
      data: OnlineUserType[];
    }
  | {
      action: 'joining' | 'leaving';
      data: OnlineUserType;
    }
  | null;

export type ProjectCommunicationContextType = {
  activeChannel: ChannelType;
  messages?: (MessageEntity | undefined)[];
  deleteMessage: (id: number) => void;
  sendMessage: (e: React.FormEvent<HTMLFormElement>) => void;
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  channels: ChannelType[];
  directChannels: ChannelType[];
  setActiveChannel: React.Dispatch<React.SetStateAction<ChannelType>>;
  loading: boolean;
  toggleGoToBottom: boolean;
  notifications: { [key: string]: number };
  handleChannelChange: (channel: ChannelType) => void;
  onlineUsers: OnlineUserType[];
};

export const ProjectCommunicationContext = createContext(
  {} as ProjectCommunicationContextType
);

export default function Communication() {
  const dispatch = useReduxAction();

  const alert = useAlert();
  const translate = useTranslate();
  const generateEventName = useGenerateEventName();

  const [channels, setChannels] = useState<ChannelType[]>([]);
  const [directChannels, setDirectChannels] = useState<ChannelType[]>([]);
  const [activeChannel, setActiveChannel] = useState<ChannelType>({
    type: 'App\\User',
    id: -1,
  });
  const [onlineUsers, setOnlineUsers] = useState<OnlineUserType[]>([]);
  const [onlineUserEvent, setOnlineUserEvent] = useState<OnlineUserEventType>(
    null
  );

  const [newMessage, setNewMessage] = useState<MessageEntity | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);
  const [toggleGoToBottom, setToggleGoToBottom] = useState(false);
  const [notifications, setNotifications] = useState<{ [key: string]: number }>(
    {}
  );
  const [inputValue, setInputValue] = useState('');

  const { project, processGroups, channelId } = useContext(
    ProjectDetailsContext
  );

  const user = useRedux((state) => state.authentication?.user);

  const token = useRedux((state) => state.authentication?.token);

  const messages = useRedux((state) =>
    state.message?.entities.allIds
      .map((id) => state.message?.entities.byId[id])
      .filter((m) => m?.message_type === 'text')
  );

  const [storedChannel, setStoredChannel] = useStorage(
    `user-${user?.id}-channel-${project?.id}`,
    activeChannel.id
  );

  const [socket, setSocket] = useState<any>(undefined);

  function checkIn() {
    return dispatch(
      storeMessage({
        target_id: activeChannel.id,
        target_type: activeChannel.type,
        isCheckIn: true,
      })
    );
  }

  function handleChannelChange(channel: ChannelType) {
    // store check in before changing channel
    if (activeChannel.id > 0) checkIn();
    setActiveChannel(channel);
  }

  function fetchChannelMessages() {
    return dispatch(
      indexMessage({
        target_id: activeChannel.id,
        target_type: activeChannel.type,
      })
    )
      .then(() => {
        setLoading(false);
        return checkIn();
      })
      .then(() => dispatch(indexNotification()));
  }

  function deleteMessage(messageId: number) {
    dispatch(updateMessage({ messageId }))
      .then(() => fetchChannelMessages())
      .catch(() => {
        alert('Could not submit message.');
      });
  }

  function sendMessage(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInputValue('');

    dispatch(
      storeMessage({
        target_type: activeChannel.type,
        target_id: activeChannel.id,
        message_type: 'text',
        message_data: inputValue,
        project_id:
          activeChannel.type === 'App\\User' && project ? project.id : '',
      })
    )
      .then((res: MessageEntity) => {
        const channel = generateEventName(res.target_type, res.target_id);
        socket?.private(channel).whisper('message', res);
        setToggleGoToBottom((prev) => !prev);
      })
      .catch(() => {
        alert('Could not submit message.');
      });
  }

  // Store channel in local storage
  useEffect(() => {
    if (activeChannel.id > 0) setStoredChannel(activeChannel.id);
  }, [activeChannel.id]);

  // Open channel on mount
  useEffect(() => {
    if (channels.length) {
      const processChannel = channels
        .map((c) =>
          c.id === storedChannel
            ? c
            : c.activities?.find((a) => a.id === storedChannel)
        )
        .filter((exists) => exists)[0];

      const personChannel = directChannels.find((c) => c.id === storedChannel);

      if (processChannel) return setActiveChannel(processChannel);
      if (personChannel) return setActiveChannel(personChannel);
      setActiveChannel(channels[0]);
    }
  }, [channels.length]);

  // check in if chat is open
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(
    () => () =>
      activeChannel.id > 0 && !isMounted.current ? checkIn() : undefined,
    [activeChannel.id]
  );

  // Step 1: map over all channels and add an event listener to each that sets the newMessage state
  useEffect(() => {
    const allChannels: ChannelType[] = [];

    allChannels.concat(channels, directChannels).forEach((main) => {
      allChannels.push(main);
      if (main.activities?.length) {
        main.activities.map((activity) => allChannels.push(activity));
      }
    });

    const channelIdList = allChannels
      .filter((c) => c)
      .map((channel) => generateEventName(channel.type, channel.id));

    if (!channelIdList.length) return;

    channelIdList.forEach((channel) => {
      if (channel.includes('project')) {
        socket
          .join(channel)
          .here((users: OnlineUserType[]) =>
            setOnlineUserEvent({ action: 'here', data: users })
          )
          .joining((newUser: OnlineUserType) =>
            setOnlineUserEvent({ action: 'joining', data: newUser })
          )
          .leaving((leavingUser: OnlineUserType) =>
            setOnlineUserEvent({ action: 'leaving', data: leavingUser })
          );

        // also subscribe to Private channel to fix GroupChat bug
        socket
          .private(channel)
          .listenForWhisper('message', (data: MessageEntity) =>
            setNewMessage(data)
          );
      } else {
        socket
          .private(channel)
          .listenForWhisper('message', (data: MessageEntity) =>
            setNewMessage(data)
          );
      }
    });

    if (channelId) {
      const currentChannel = allChannels.find((c) => `${c.id}` === channelId);
      if (currentChannel) setActiveChannel(currentChannel);
    }

    return function cleanup() {
      channelIdList.map((channel) => socket.leave(channel));
    };
  }, [channels.length, directChannels.length]);

  // Step 2: trigger function that pushes message and ads notifications
  useEffect(() => {
    if (newMessage) {
      const getTarget = () => {
        if (
          newMessage.target_type === 'App\\User' &&
          user?.id === parseInt(newMessage.target_id as any)
        ) {
          return generateEventName(
            newMessage.target_type,
            parseInt(newMessage.message_author as any)
          );
        }

        return generateEventName(
          newMessage.target_type,
          parseInt(newMessage.target_id as any)
        );
      };

      const target = getTarget();
      const current = generateEventName(activeChannel.type, activeChannel.id);

      if (target !== current) {
        setNotifications((prev) => ({
          ...prev,
          [target]: prev?.[target] !== undefined ? prev?.[target] + 1 : 1,
        }));
      } else {
        dispatch(storeMessage({ ...newMessage, fromSocket: true }));
      }
    }
  }, [newMessage?.id]);

  // Delete unread count if you select channel
  useEffect(() => {
    if (activeChannel.id < 0) return;

    setLoading(true);
    fetchChannelMessages();

    const targetEvent = generateEventName(activeChannel.type, activeChannel.id);

    if (notifications[targetEvent]) {
      setNotifications((prev) => ({ ...prev, [targetEvent]: 0 }));
    }
  }, [activeChannel.id]);

  const phases = useRedux((state) =>
    state.phase?.entities.allIds.map((id) => state.phase?.entities.byId[id])
  );

  const activities = useRedux((state) =>
    state.activity?.entities.allIds.map(
      (id) => state.activity?.entities.byId[id]
    )
  );

  const courses = useRedux((state) =>
    state.training?.entities.allIds.map(
      (id) => state.training?.entities.byId[id]
    )
  );

  useEffect(() => {
    if (onlineUserEvent) {
      switch (onlineUserEvent.action) {
        case 'here':
          setOnlineUsers(onlineUserEvent.data);
          break;
        case 'joining':
          setOnlineUsers((prev) => [...prev, onlineUserEvent.data]);
          break;
        case 'leaving':
          setOnlineUsers((prev) =>
            prev.filter((u) => u.id !== onlineUserEvent.data.id)
          );
          break;
        default:
          break;
      }
    }
  }, [onlineUserEvent]);

  // Mount channels
  useEffect(() => {
    // @ts-ignore
    const pusher = echo(token?.access_token);
    setSocket(pusher);

    const newChannels: ChannelType[] = [];
    processGroups.forEach((phase) => {
      if (!phase.processId) return;

      newChannels.push({
        type: 'App\\ProjectProcess',
        id: phase.processId,
        name: phases?.find((p) => p?.id === phase.id)?.name,
        image: phases?.find((p) => p?.id === phase.id)?.thumbnail,
        phase: phase.phase,
        activities: phase?.activities
          .filter((a) => a.process_type !== 'App\\Phase')
          .map((activity) => ({
            type: 'App\\ProjectProcess',
            id: activity.processId!,
            name:
              activity.process_type === 'App\\Training'
                ? courses?.find((c) => c?.id === activity.id)?.title
                : activities?.find((a) => a?.id === activity.id)?.name,
            phase: phase.phase,
            image:
              activity.process_type === 'App\\Training'
                ? '/images/training-icon.svg'
                : activities?.find((a) => a?.id === activity.id)?.thumbnail,
          })),
      });
    });
    setChannels(newChannels);

    const newDirectChannels: ChannelType[] = [];
    if (project?.members?.length) {
      newDirectChannels.push({
        id: project.id,
        type: 'App\\Project',
        name: `${translate('general.terms.group')}`,
        image: '/images/group-icon.svg',
      });

      const members = project.members.map((member) => member.user);

      members
        .filter(
          (member) =>
            member.id !== user?.id &&
            !member.roles.some((r) => r.name === 'Extern') // remove extern from chat list
        )
        .forEach((member) => {
          newDirectChannels.push({
            id: member.id,
            type: 'App\\User',
            name: `${member.first_name} ${member.last_name}`,
            member,
          });
        });
    }

    setDirectChannels(newDirectChannels);
  }, []);

  const contextValues = {
    activeChannel,
    messages,
    deleteMessage,
    sendMessage,
    inputValue,
    setInputValue,
    channels,
    directChannels,
    setActiveChannel,
    loading,
    toggleGoToBottom,
    notifications,
    handleChannelChange,
    onlineUsers,
  };

  return (
    <ProjectCommunicationContext.Provider value={contextValues}>
      <div styleName="container">
        <ChatList />
        <Chat {...contextValues} />
      </div>
    </ProjectCommunicationContext.Provider>
  );
}
